import keys from '@debbie/common/dist/cache/keys.js';
import { Entity } from '@debbie/common/dist/index.js';
import { PropertyDataType, } from '@debbie/common/dist/properties/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { randomToken } from '@debbie/common/dist/util/index.js';
import { properties as propertiesAPI } from '@debbie/cortex/dist/api/index.js';
import { propertyTypesEntity } from '@debbie/cortex/dist/properties/index.js';
import q from '@debbie/cortex/dist/query.js';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification, getErrorMessage, successNotification, } from '../../utils/errors.js';
import { Button, Checkbox, Errors, Input, Select, Textarea, Well, } from '../index.js';
import PropertyScopeSelect from './PropertyScopeSelect.js';
import PropertyTypeInfo from './PropertyTypeInfo.js';
const refOptions = [
    Entity.Case,
    Entity.CaseVoucher,
    Entity.Creditor,
    Entity.Customer,
];
const NewPropertyTypeCMP = (props) => {
    const lang = useSelector((s) => s.lang);
    const [dropdownOptions, setDropdownOptions] = useState(lang.properties.defaultDropdownOptions);
    const [newPropertyType, setNewPropertyType] = useState({
        data: null,
        handle: randomToken(),
        label: props.initialValues?.label ?? '',
        description: '',
        type: null,
        required: false,
        scopes: props.initialValues?.scopes ?? [],
        ref: props.initialValues?.ref ?? null,
        permissionLevel: 'caseworker',
        permissionLevelEdit: 'caseworker',
    });
    const options = dropdownOptions.split(`\n`).filter((option) => option.trim());
    const property = newPropertyType.type === null || newPropertyType.ref === null
        ? undefined
        : {
            ...newPropertyType,
            type: newPropertyType.type,
            ref: newPropertyType.ref,
            label: newPropertyType.label.trim(),
            description: newPropertyType.description?.trim(),
            data: newPropertyType.type === PropertyDataType.dropdown
                ? {
                    options: options.map((option) => ({
                        value: option.trim(),
                        text: option.trim(),
                    })),
                }
                : null,
        };
    const createMutation = useMutation(async (details) => {
        await propertiesAPI.createType(details);
        return details;
    }, {
        onSuccess: (createdProperty) => {
            q.invalidate(keys.write.create(propertyTypesEntity));
            successNotification(lang, 'create', createdProperty.label);
            props.onAdd(createdProperty);
        },
        onError: (error) => errorNotification(lang, error, 'create', newPropertyType.label),
    });
    return (React.createElement(PropertiesStyled, { disabled: createMutation.isLoading },
        React.createElement(RefLabel, null, lang.properties.ref),
        React.createElement(RefCards, null, (props.allowedRefs ?? refOptions).map((ref) => {
            const selected = newPropertyType.ref === ref;
            return (React.createElement(RefCard, { key: ref, type: "button", $selected: selected, "aria-pressed": selected, onClick: () => {
                    setNewPropertyType({
                        ...newPropertyType,
                        ref,
                        scopes: [Entity.Case, Entity.CaseVoucher].includes(ref)
                            ? newPropertyType.scopes
                            : [],
                    });
                } },
                React.createElement(RefCardTitle, { $selected: selected }, lang.entities[ref]),
                React.createElement(RefCardDescription, null, lang.propertyRefDescriptions[ref])));
        })),
        (newPropertyType.ref === Entity.Case ||
            newPropertyType.ref === Entity.CaseVoucher) && (React.createElement(PropertyScopeSelect, { scopes: newPropertyType.scopes ?? [], onChange: (scopes) => setNewPropertyType({ ...newPropertyType, scopes }) })),
        React.createElement(Input, { autofocus: true, placeholder: lang.properties.label, onChange: (e) => {
                setNewPropertyType({
                    ...newPropertyType,
                    label: e.target.value,
                });
            }, value: newPropertyType.label }),
        React.createElement(Textarea, { placeholder: lang.properties.description, onChange: (e) => {
                setNewPropertyType({
                    ...newPropertyType,
                    description: e.target.value,
                });
            }, value: newPropertyType.description }),
        React.createElement(Checkbox, { placeholder: lang.properties.required, onChange: (e) => {
                setNewPropertyType({
                    ...newPropertyType,
                    required: e.target.checked,
                });
            }, checked: newPropertyType.required }),
        React.createElement("br", null),
        React.createElement(Select, { label: lang.properties.selectType, searchPlaceholder: lang.misc.search, options: Object.values(PropertyDataType).map((type) => ({
                value: type,
                label: lang.propertyDataTypes[type],
                description: lang.propertyDataTypeDescriptions[type],
            })), selected: newPropertyType.type != null ? [newPropertyType.type] : [], onChange: (selected) => {
                setNewPropertyType({
                    ...newPropertyType,
                    type: selected[0] ?? null,
                });
            } }),
        newPropertyType.type === PropertyDataType.dropdown && (React.createElement(Textarea, { placeholder: lang.properties.dropdownOptions, value: dropdownOptions, onChange: (e) => {
                setDropdownOptions(e.target.value);
            } })),
        property !== undefined && (React.createElement(React.Fragment, null,
            React.createElement(Well, null,
                React.createElement(PropertyTypeInfo, { property: property })),
            React.createElement("br", null))),
        createMutation.isError && (React.createElement(Errors, { errors: [
                getErrorMessage(createMutation.error) ?? lang.misc.errorCreating,
            ] })),
        React.createElement(Button, { onClick: () => {
                if (property !== undefined) {
                    void createMutation.mutate(property);
                }
            }, loading: createMutation.isLoading, disabled: !(property?.label &&
                newPropertyType.handle &&
                property.description &&
                newPropertyType.type &&
                newPropertyType.ref &&
                (newPropertyType.type !== PropertyDataType.dropdown ||
                    (newPropertyType.type === PropertyDataType.dropdown &&
                        options.length > 0))) }, lang.properties.create)));
};
export default NewPropertyTypeCMP;
const PropertiesStyled = styled.fieldset `
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
`;
const RefLabel = styled.div `
  font-size: 0.85em;
  font-weight: 600;
  margin-bottom: 8px;
`;
const RefCards = styled.div `
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: ${THEME.GRID_SPACING}px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
const RefCard = styled.button `
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  text-align: left;
  border-radius: 6px;
  border: 1px solid
    ${(p) => (p.$selected ? THEME.COLOR_ACCENT : THEME.BORDER_STRONG)};
  box-shadow: ${(p) => p.$selected ? `0 0 0 1px ${THEME.COLOR_ACCENT}` : 'none'};
  background: ${(p) => (p.$selected ? THEME.SURFACE_INSET : '#ffffff')};
  cursor: pointer;
  outline: none;
  transition:
    border-color 0.12s ease,
    box-shadow 0.12s ease,
    background 0.12s ease;

  &:hover {
    border-color: ${THEME.COLOR_ACCENT};
  }
`;
const RefCardTitle = styled.span `
  font-weight: 700;
  color: ${(p) => (p.$selected ? THEME.COLOR_ACCENT : '#2b2b33')};
`;
const RefCardDescription = styled.span `
  font-size: 0.85em;
  line-height: 1.35;
  color: ${THEME.TEXT_MUTED};
`;
//# sourceMappingURL=NewPropertyType.js.map